import {
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Box,
  Checkbox,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";

/**
 * Default plain-text display renderer used by text/number/select field types.
 *
 * @param {{ value: unknown }} props
 * @returns {JSX.Element}
 */
export const TextDisplayCell = ({ value }) => {
  return (
    <Typography
      variant='body2'
      sx={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
      {value == null ? "" : String(value)}
    </Typography>
  );
};

/**
 * Default single-line text editor.
 *
 * @param {{ value: unknown, onCommit: (value: unknown) => void, onCancel: () => void }} props
 * @returns {JSX.Element}
 */
export const TextEditorCell = ({ value, onCommit, onCancel }) => {
  return (
    <TextField
      variant='standard'
      size='small'
      fullWidth
      autoFocus
      defaultValue={value ?? ""}
      sx={{ width: "100%" }}
      onBlur={(event) => onCommit(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          onCommit(event.target.value);
        }
        if (event.key === "Escape") {
          onCancel();
        }
      }}
    />
  );
};

/**
 * Default numeric editor. Actual parsing/coercion is handled by column `parseValue`.
 *
 * @param {{ value: unknown, onCommit: (value: unknown) => void, onCancel: () => void, column?: object }} props
 * @returns {JSX.Element}
 */
export const NumberEditorCell = ({ value, onCommit, onCancel, column }) => {
  return (
    <TextField
      variant='standard'
      size='small'
      type='number'
      fullWidth
      autoFocus
      defaultValue={value ?? ""}
      sx={{ width: "100%" }}
      inputProps={{
        step: column?.editorProps?.step ?? "any",
        min: column?.editorProps?.min,
        max: column?.editorProps?.max,
      }}
      onBlur={(event) => onCommit(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          onCommit(event.target.value);
        }
        if (event.key === "Escape") {
          onCancel();
        }
      }}
    />
  );
};

/**
 * Default select editor supporting single-select and multi-select modes.
 *
 * @param {{ value: unknown, onCommit: (value: unknown) => void, onCancel: () => void, column?: object }} props
 * @returns {JSX.Element}
 */
export const SelectEditorCell = ({ value, onCommit, onCancel, column }) => {
  const options = column?.editorProps?.options ?? [];
  const isMultiSelect = Boolean(column?.editorProps?.multiselect);

  return (
    <Autocomplete
      size='small'
      multiple={isMultiSelect}
      options={options}
      autoFocus
      freeSolo={Boolean(column?.editorProps?.freeSolo)}
      value={value ?? (isMultiSelect ? [] : null)}
      onChange={(_, nextValue) => {
        onCommit(nextValue);
      }}
      onBlur={() => {
        if (!isMultiSelect) {
          onCommit(value ?? null);
        }
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={String(option)}
            size='small'
            {...getTagProps({ index })}
            key={`${option}-${index}`}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant='standard'
          placeholder={column?.editorProps?.placeholder || "Select"}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              onCancel();
            }
          }}
        />
      )}
      disableCloseOnSelect={isMultiSelect}
      sx={{ minWidth: 140 }}
    />
  );
};

/**
 * Default boolean display renderer.
 *
 * @param {{ value: unknown }} props
 * @returns {JSX.Element}
 */
export const BoolDisplayCell = ({ value }) => {
  const isTrue = value === true;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}>
      {isTrue ? <Check fontSize='small' /> : <Close fontSize='small' />}
    </Box>
  );
};

/**
 * Default boolean editor using a centered checkbox.
 *
 * @param {{ value: unknown, onCommit: (value: unknown) => void, onCancel: () => void }} props
 * @returns {JSX.Element}
 */
export const BoolEditorCell = ({ value, onCommit, onCancel }) => {
  const checked = value === true;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}>
      <Checkbox
        size='small'
        autoFocus
        checked={checked}
        onChange={(event) => {
          onCommit(Boolean(event.target.checked));
        }}
        onBlur={() => {
          onCommit(checked);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onCancel();
          }
        }}
        inputProps={{ "aria-label": "Boolean editor" }}
      />
    </Box>
  );
};

const FIELD_TYPE_COMPONENTS = {
  text: {
    displayComponent: TextDisplayCell,
    editComponent: TextEditorCell,
  },
  number: {
    displayComponent: TextDisplayCell,
    editComponent: NumberEditorCell,
  },
  select: {
    displayComponent: TextDisplayCell,
    editComponent: SelectEditorCell,
  },
  bool: {
    displayComponent: BoolDisplayCell,
    editComponent: BoolEditorCell,
  },
};

/**
 * Applies default display/editor/parse behaviors based on a column `fieldType`.
 * Existing explicit column handlers always take precedence.
 *
 * @param {object} column
 * @returns {object}
 */
export const resolveDefaultColumnComponents = (column) => {
  const fieldType = column?.fieldType;
  if (!fieldType || !FIELD_TYPE_COMPONENTS[fieldType]) {
    return column;
  }

  const defaults = FIELD_TYPE_COMPONENTS[fieldType];
  const defaultParseValue =
    fieldType === "number"
      ? (rawValue) => {
          if (rawValue === "" || rawValue == null) {
            return null;
          }
          const parsed = Number(rawValue);
          return Number.isNaN(parsed) ? rawValue : parsed;
        }
      : fieldType === "select" && column?.editorProps?.multiselect
        ? (rawValue) =>
            Array.isArray(rawValue)
              ? rawValue
              : rawValue == null
                ? []
                : [rawValue]
        : fieldType === "bool"
          ? (rawValue) => {
              if (typeof rawValue === "boolean") {
                return rawValue;
              }
              if (rawValue == null) {
                return false;
              }
              if (typeof rawValue === "string") {
                const normalized = rawValue.trim().toLowerCase();
                if (["true", "1", "yes", "y", "on"].includes(normalized)) {
                  return true;
                }
                if (["false", "0", "no", "n", "off", ""].includes(normalized)) {
                  return false;
                }
              }
              return Boolean(rawValue);
            }
          : undefined;

  return {
    ...column,
    displayComponent: column.displayComponent || defaults.displayComponent,
    editComponent: column.editComponent || defaults.editComponent,
    parseValue: column.parseValue || defaultParseValue,
  };
};
